import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw, Clock } from 'lucide-react';
import { evidenceExpiryApi, ExpiringEvidence, RenewalTask, ValidityPeriod, ExpiryDashboard } from '../api/evidenceExpiry.api';
import { useAuth } from '@contexts/AuthContext';

type TabKey = 'expiring' | 'expired' | 'renewal' | 'validity';

const EvidenceExpiryDashboard: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState<TabKey>('expiring');
  const [dashboard, setDashboard] = useState<ExpiryDashboard | null>(null);
  const [expiring, setExpiring] = useState<ExpiringEvidence[]>([]);
  const [expired, setExpired] = useState<ExpiringEvidence[]>([]);
  const [renewalTasks, setRenewalTasks] = useState<RenewalTask[]>([]);
  const [validityPeriods, setValidityPeriods] = useState<ValidityPeriod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingValidity, setEditingValidity] = useState<Record<number, { validity_days: number; review_days_before: number }>>({});
  const [saving, setSaving] = useState<number | null>(null);

  const companyId = user?.company_id ?? undefined;

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [dash, exp30, exp0, tasks, validity] = await Promise.all([
        evidenceExpiryApi.getDashboard(companyId),
        evidenceExpiryApi.getExpiring(30, companyId),
        evidenceExpiryApi.getExpired(companyId),
        evidenceExpiryApi.listRenewalTasks(companyId),
        evidenceExpiryApi.getValidityPeriods(companyId),
      ]);
      setDashboard(dash);
      setExpiring(exp30);
      setExpired(exp0);
      setRenewalTasks(tasks);
      setValidityPeriods(validity);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const handleCompleteRenewal = async (id: number) => {
    try {
      await evidenceExpiryApi.completeRenewalTask(id);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleSaveValidity = async (id: number) => {
    const data = editingValidity[id];
    if (!data) return;
    setSaving(id);
    try {
      await evidenceExpiryApi.updateValidityPeriod(id, data);
      setEditingValidity(prev => { const n = { ...prev }; delete n[id]; return n; });
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'expiring', label: 'Expiring Soon' },
    { key: 'expired', label: 'Expired' },
    { key: 'renewal', label: 'Renewal Tasks' },
    { key: 'validity', label: 'Validity Periods' },
  ];

  const getDaysClass = (days: number) => {
    if (days <= 7) return 'text-red-600 font-semibold';
    if (days <= 14) return 'text-orange-600';
    return 'text-yellow-600';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Evidence Expiry Management</h1>
        <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Expiring in 30 days', value: dashboard.expiring_30_days, icon: Clock, color: 'text-yellow-600' },
            { label: 'Expiring in 7 days', value: dashboard.expiring_7_days, icon: AlertTriangle, color: 'text-orange-600' },
            { label: 'Expired', value: dashboard.expired, icon: AlertTriangle, color: 'text-red-600' },
            { label: 'Renewal Tasks Pending', value: dashboard.renewal_tasks_pending, icon: CheckCircle, color: 'text-blue-600' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">{label}</span>
                <Icon size={16} className={color} />
              </div>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="border-b flex gap-1">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : (
        <>
          {tab === 'expiring' && (
            <div className="bg-white border rounded-xl overflow-hidden">
              {expiring.length === 0 ? <div className="py-12 text-gray-500">No evidence expiring soon.</div> : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>{['File Name', 'Page', 'Expires At', 'Days Remaining'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y">
                    {expiring.map(e => (
                      <tr key={e.id}>
                        <td className="px-4 py-3 text-left font-medium text-gray-900">{e.file_name}</td>
                        <td className="px-4 py-3 text-left text-gray-600">Page #{e.compliance_page_id}</td>
                        <td className="px-4 py-3 text-left text-gray-600">{new Date(e.expires_at).toLocaleDateString()}</td>
                        <td className={`px-4 py-3 ${getDaysClass(e.days_remaining)}`}>{e.days_remaining} days</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {tab === 'expired' && (
            <div className="bg-white border rounded-xl overflow-hidden">
              {expired.length === 0 ? <div className="py-12 text-gray-500">No expired evidence.</div> : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>{['File Name', 'Page', 'Expired At'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y">
                    {expired.map(e => (
                      <tr key={e.id} className="bg-red-50">
                        <td className="px-4 py-3 text-left font-medium text-gray-900">{e.file_name}</td>
                        <td className="px-4 py-3 text-left text-gray-600">Page #{e.compliance_page_id}</td>
                        <td className="px-4 py-3 text-left text-red-600">{new Date(e.expires_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {tab === 'renewal' && (
            <div className="bg-white border rounded-xl overflow-hidden">
              {renewalTasks.length === 0 ? <div className="py-12 text-gray-500">No renewal tasks.</div> : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>{['Evidence', 'Due Date', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y">
                    {renewalTasks.map(t => (
                      <tr key={t.id}>
                        <td className="px-4 py-3 text-left text-gray-900">{t.evidence_name || `Evidence #${t.evidence_item_id}`}</td>
                        <td className="px-4 py-3 text-left text-gray-600">{t.due_date ? new Date(t.due_date).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3 text-left">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${t.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {t.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-left">
                          {t.status !== 'completed' && (
                            <button onClick={() => handleCompleteRenewal(t.id)} className="text-green-600 hover:underline text-xs">
                              Mark Complete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {tab === 'validity' && (
            <div className="bg-white border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>{['Category', 'Validity (days)', 'Review Before (days)', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y">
                  {validityPeriods.map(vp => {
                    const editing = editingValidity[vp.id];
                    return (
                      <tr key={vp.id}>
                        <td className="px-4 py-3 text-left font-medium text-gray-900 capitalize">{vp.category}</td>
                        <td className="px-4 py-3 text-left">
                          {editing ? (
                            <input type="number" value={editing.validity_days} onChange={e => setEditingValidity(p => { const cur = p[vp.id]; return { ...p, [vp.id]: { validity_days: Number(e.target.value), review_days_before: cur?.review_days_before ?? vp.review_days_before } }; })}
                              className="border rounded px-2 py-1 w-24 text-sm" />
                          ) : vp.validity_days}
                        </td>
                        <td className="px-4 py-3 text-left">
                          {editing ? (
                            <input type="number" value={editing.review_days_before} onChange={e => setEditingValidity(p => { const cur = p[vp.id]; return { ...p, [vp.id]: { validity_days: cur?.validity_days ?? vp.validity_days, review_days_before: Number(e.target.value) } }; })}
                              className="border rounded px-2 py-1 w-24 text-sm" />
                          ) : vp.review_days_before}
                        </td>
                        <td className="px-4 py-3 text-left">
                          {editing ? (
                            <div className="flex gap-2">
                              <button onClick={() => handleSaveValidity(vp.id)} disabled={saving === vp.id}
                                className="text-green-600 hover:underline text-xs">{saving === vp.id ? 'Saving...' : 'Save'}</button>
                              <button onClick={() => setEditingValidity(p => { const n = { ...p }; delete n[vp.id]; return n; })}
                                className="text-gray-500 hover:underline text-xs">Cancel</button>
                            </div>
                          ) : (
                            <button onClick={() => setEditingValidity(p => ({ ...p, [vp.id]: { validity_days: vp.validity_days, review_days_before: vp.review_days_before } }))}
                              className="text-blue-600 hover:underline text-xs">Edit</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EvidenceExpiryDashboard;
